/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  getAssetType,
  getAssetUri,
  getFixedWidthValues,
  getMinZoomFromAllPages,
  getMaxZoomFromAllPages,
  calculateMaxZoomForIEEdge,
  getTitleFromConfigProp,
  calculateZoomLevelAccToCurrPageRasterSize,
  getPageHeightAndWidthFromPageGeometry,
  getOriginalPageDimensions
} from "app/AppInitUtility";
import docApi from "api";
import { getLastPartFromPath, getImageDimensionsFromBlobUrl } from "utils/UrlUtils";
import {
  getQueryParameterFromUrl,
  createURLFromBuffer,
  getUrlVars,
  getNamePartsFromFullName
} from "dc-preview-utils/src/Utils/UrlUtils";
import { noop, isUndefined } from "utils/lodashUtils";
import { dialogApi, dialogTypes } from "components/dialogs";
import {
  getTotalPages,
  getFileSize,
  getShouldRenderAcroForm,
  isAlternateAssetURIAvailable,
  getIsLinearizedWorkflow,
  getDocPermissions,
  getDocumentType,
  getPageRenditionCallInfo,
  getCacheHitAndMissCount,
  getUseReactVirtualized
} from "selectors/documentSelectors";
import * as constants from "constants/constants";
import { getDevicePixelRatio, isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  getMaxZoom,
  getPageZoomLevel,
  getCurrentPageZoomLevel,
  getMinZoomLevelForMobile,
  getPageViewModifier,
  shouldAllowInitialZoomLevelChange,
  getIsCommentsPanelVisible
} from "selectors/zoom";
import { subscribe } from "utils/keyboard";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { isInitialPageLoaded, getInitialPageIndex } from "selectors/pagedViewState";
import {
  loadStatus,
  errorCodes,
  loadFailureType,
  pageViewModifiers,
  docViewModes,
  httpErrorCodes,
  noPreviewErrorCodes,
  AJSMetadataStatus,
  lhpFeatures,
  dcapiMetadataFailure,
  SecondaryMetadataStatus,
  securityType,
  AnnotType
} from "constants/enums";
import { getPagesCacheManager } from "cache/cacheProvider";
// Right now using performance logging enabled condition for testapis
import { docAPIResponseHandler } from "utils/docAPIResponseHandler";
import { initJSEngine } from "pdfActionManager";
import {
  trackAJSLoad,
  trackDocumentLoadFailed,
  trackAJSMetadataFailure,
  trackFileSize,
  trackAJSMetadataDone,
  trackHasCommentsdata,
  trackHasBookmarksMetadata,
  trackHasDigSigMetadata,
  trackHasAttachmentsMetadata,
  trackPDFTitleMetadata,
  trackNamedDestinationCountMetadata,
  trackHasBaseURLMetadata,
  trackHasValidLinDictMetadata,
  trackLinDictEKeyValueMetadata,
  trackIsLinearizedWorkflow,
  trackLanguageMetadata,
  trackProducerMetadata,
  trackPageCountMetadata,
  trackDocumentSizeMetaData,
  trackPasswordDialogEvents,
  trackSecurityType,
  trackOverprintType,
  trackCreationDateMetadata,
  trackIsTagged,
  trackDocumentType,
  trackDocLevelJSTriggerPoints,
  trackCreatorMetadata,
  trackPermissionsMetadata,
  trackDocLevelNonJSActions,
  sendPageLoadEventData,
  trackIsScannedDocument,
  resetFlagsForOneTimeEvents
} from "analytics/events";
import {
  recordMainScriptLoadTime,
  recordMetadataStart,
  recordMetadataEnd,
  recordPageLoadStartTime,
  recordFirstRasterLoaded,
  getFirstRasterLoadTime,
  getPreviewInitTime,
  recordExternalMetadataEnd,
  recordExternalPageLoadStartTime,
  recordExternalPageLoadEndTime,
  recordDocJsEngineInitStart,
  recordDocJsEngineInitEnd,
  recordPageJsEngineInitStart,
  recordPageJsEngineInitEnd
} from "utils/performance";
import { pagesInView, thumbnailsInView } from "selectors/pagedViewSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { isFirstDCAPIRenditionLoaded, getDCAPIRenditionSize, getDCAPIRenditionFormat } from "selectors/dcapiSelectors";
import { getPageBeforeOrAtOffset } from "selectors/pageNumber";
import { getPagesViewXPos, getOpenAction } from "selectors/pagedViewState";
import { getPageHeight, getPageWidth } from "selectors/pageSelectors";
import { getCurrentPageIndex, getCurrentPageNumber } from "selectors/pageNumber";
import { ajsMakeCancellablePromise } from "dc-preview-utils/src/Utils/AJSCancellablePromise";
import { runInAction } from "mobx";
import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { executeDocOpenActions, performAppSetWidthOpenAction } from "pdfActionManager/docActionsHelper";
import {
  logFirstRasterRequested,
  logFirstRasterRendition,
  logAJSRenderingStart,
  logSecondaryMetadataFailed,
  logFirstRenditionTypeVisible,
  logJSActionsData,
  logNamedDestinationData,
  logNonJSActionTriggerPoints,
  logAcroformLoadTime,
  logAJSInfo,
  logGetPageRenditionCallInfo,
  logCacheHitAndMissInfo,
  logDocumentAge,
  logKeyPressBeforeDropinLoad,
  logKeyDownForEditShortcutCaretModeFeature,
  resetKeyPressLogging
} from "logging";
import { getFeatureFlagApis } from "../publicApis/featureFlag";
import { getAnnotationFlags, getHiddenOCGs } from "selectors/documentSelectors";
import { currLHPFeature } from "selectors/lhpSelectors";
import { DEFAULT_PAGE_HEIGHT, DEFAULT_PAGE_WIDTH, GET_PAGE_RENDITION_LOG_TIMEOUT } from "constants/constants";
import { getDefaultZoomLevel } from "utils/ZoomUtils";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import { isExternalAsset } from "utils/AssetUtils";
import { isIntegrationContext } from "utils/common";
import { getAppContext } from "../selectors/appUiStateSelectors";
import { getViewPortWidth, getViewPortWidthWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import { isFirstExternalAPIRenditionLoaded } from "selectors/externalSelectors";
import { triggerCompletePDFBufferAvailableEvent, triggerPreviewErrorEvent } from "publicApis/Events/events";
import {
  getShouldUseModernViewer,
  getOverlaysManager,
  getSuppressPasswordDialog,
  getIsMultiDocModeEnabled,
  getShouldDisableProtectedPdfCancel
} from "selectors/appSelectors";
import { getHCColorPallete } from "selectors/highContrastSelectors";
import { failureType } from "dc-preview-utils/src/Constants/enums";
import { PDF_ACTIONS } from "constants/enums";
import { populatePagesGeometryData } from "utils/PageUtils";
import { getAcroformDropinMountedPromise, isDropinLoaded } from "selectors/overlayDropinSelectors";
import { trackDocAndFormJSActions, getDocLevelJsTriggerPoints } from "utils/pdfActionUtils";
import { showDocPermissionsToast } from "utils/DocPermissionsUtils";
import { getHasDigSigFromMetadata } from "selectors/digSigSelectors";
import { providers, envUtil } from "dc-core";
import { VERB_QANDA } from "../constants/enums";
import { isReviewFile } from "../selectors/documentSelectors";
import { getExpandedPanelWidth } from "../utils/SkinnyRailUtils";
import { enableCommentingOnMobile } from "../utils/MobileMvUtils";
import { getMinMaxZoomLimits, getMinZoomFromDefaultAndMaxZoom } from "providers/previewProvider/ZoomUtilsLite";
import { setHasPreviewDropinInit } from "providers/previewProvider/PreviewProviderUtils";
import { getCaretMode } from "selectors/caretModeSelectors";
import { getIsCursorVisible } from "dc-preview-utils/src/selectors/cursorSelectors";

class RenderingManager {
  constructor(stores, props) {
    this.stores = stores;
    this.props = props;
    this.shouldOpenInFixedWidthMode = false;
    this._passwordAttempts = 0;
    this._pagesBeingPreloaded = [];
    this._sendKeyboardEventsToOverlayDropins = this._sendKeyboardEventsToOverlayDropins.bind(this);
    /**
     * DCAPI metadata does not have numPages at some points and so we retry.
     * Maximum number of calls to make to dcapi for metadata in case of non pdf.
     */
    this.dcapiMetadataTries = 0;
    this.externalMetadataTries = 0;
    this.sessionTime = 0;

    this._getMetadataFailed = this._getMetadataFailed.bind(this);
    this._onPasswordSubmit = this._onPasswordSubmit.bind(this);
    this._getMetadataAJSDone = this._getMetadataAJSDone.bind(this);
    this._getMetadataDCAPIDone = this._getMetadataDCAPIDone.bind(this);
    this._getMetadataExternalDone = this._getMetadataExternalDone.bind(this);
    this._sendKeyboardEventsToOverlayDropins = this._sendKeyboardEventsToOverlayDropins.bind(this);
    this._getExternalMetadataFailed = this._getExternalMetadataFailed.bind(this);
    this._getDCAPIMetadataFailed = this._getDCAPIMetadataFailed.bind(this);

    this.ajsGetMetaDataPromise = this._ajsGetMetaDataPromise();
    this.shouldSupportPersonalComments = isPersonalCommentingEnabled(stores);
    this._setShouldOpenInFixedWidthMode();
    this.ajsGetMetaDataPromise.catch(noop);
    this.isJSEngineLoaded = false;
    this.cacheHitAndMissLogTime = 1 * 60 * 1000; // 1 min
    this._logCacheHitAndMissInfo = this._logCacheHitAndMissInfo.bind(this);
    this.selectivelyRenderAnnotsFlag = null;
    resetKeyPressLogging();
    resetFlagsForOneTimeEvents();
  }

  async resetAssetURIInStore(props) {
    const { assetUri: oldAssetUri } = this.props.config;
    this.props = props;

    let { assetUri } = this.props.config;

    // Remap AJS rendition cache in rendition-provider layer. Also delete secondary metadata of old asset.
    await docApi.remapAJSCache(assetUri);
    await docApi.clearRenditionCache({
      assetUri: oldAssetUri,
      useAJS: true,
      useDCAPI: true,
      enableExternal: true
    });

    // Reset assetURI in DocAPIDropin
    docApi.resetAssetURI(assetUri);
    this._setAssetUri(assetUri, null);

    const metaDataPromise = docApi.getMetaData({ _useAJS: false });

    let externalMetaData;

    if (metaDataPromise.external) {
      externalMetaData = await metaDataPromise.external;
    }

    // Updating the external metadata. Also the fileName (As copied asset differs in fileName only)
    if (externalMetaData) {
      this.stores.documentStore.setFileName(externalMetaData.fileName);
      this.stores.documentStore.setFileMetaData(externalMetaData);
    }
  }

  init(switchPreview = false) {
    const { path } = this.props.config;
    let promises = [];

    this._initDocApi(this.props.config, switchPreview);
    promises.push(docApi.ready());
    if (isReviewFile(this.stores, envUtil) && !getIsMultiDocModeEnabled(this.stores)) {
      this.stores.skinnyRailStore.setFitWidthModeForSharedFiles();
    }

    ajsMakeCancellablePromise(Promise.all(promises)).then(() => {
      recordMetadataStart();
      setHasPreviewDropinInit(true);

      const getMetaDataPromise = docApi.getMetaData({});

      docAPIResponseHandler(
        getMetaDataPromise,
        this._getMetadataAJSDone,
        this._getMetadataFailed,
        isExternalAsset(path) ? this._getMetadataExternalDone : this._getMetadataDCAPIDone,
        isExternalAsset(path) ? this._getExternalMetadataFailed : this._getDCAPIMetadataFailed
      );

      docAPIResponseHandler(getMetaDataPromise, this.ajsGetMetaDataResolve, this.ajsGetMetaDataReject, () => {});
    });

    recordMainScriptLoadTime();
    // track ajs load when we have nav start from viewing site
    Promise.all(promises).then(() => trackAJSLoad());
  }

  _ajsGetMetaDataPromise() {
    return new Promise((resolve, reject) => {
      this.ajsGetMetaDataResolve = resolve;
      this.ajsGetMetaDataReject = reject;
    });
  }

  _setAssetUri(assetUri, alternateAssetUri) {
    this.stores.documentStore.setAssetURI(assetUri);
    if (alternateAssetUri) {
      this.stores.documentStore.setAlternateAssetURI(alternateAssetUri);
      this.stores.documentStore.setIsAlternateAssetURIAvailable(true);
    }
  }

  _initDocApi(config, switchPreview) {
    let { assetUri, alternateAssetUri, path, filetype } = config;

    assetUri = assetUri || getAssetUri();
    let assetType = path || getAssetType();
    filetype = filetype || decodeURIComponent(getQueryParameterFromUrl(window.location.href, "filetype"));

    docApi.init({
      assetUri,
      assetType,
      filetype,
      isRichBuild: isPersonalCommentingEnabled(this.stores) || getShouldRenderAcroForm(this.stores),
      switchPreview
    });

    this._setAssetUri(assetUri, alternateAssetUri);
  }

  _setShouldOpenInFixedWidthMode() {
    const { fixedWidthLeftMargin, fixedWidthRightMargin } = getFixedWidthValues(this.stores, this.props.config);
    const shouldUseModernViewer = getShouldUseModernViewer(this.stores);
    if (
      !shouldUseModernViewer &&
      isPersonalCommentingEnabled(this.stores) &&
      !isUndefined(fixedWidthLeftMargin) &&
      !isUndefined(fixedWidthRightMargin)
    ) {
      this.stores.zoom.setProvidedFixedWidthMargin(fixedWidthLeftMargin, fixedWidthRightMargin);
      this.shouldOpenInFixedWidthMode = false; // switching default commentsList
    }

    if (isPersonalCommentingEnabled(this.stores) && isCurrentDeviceMobile() && this.shouldOpenInFixedWidthMode) {
      this.shouldOpenInFixedWidthMode = false;
    }
  }

  _sendKeyboardEventsToOverlayDropins(evt) {
    overlayEvents.trigger("keydown", evt);
    logKeyDownForEditShortcutCaretModeFeature(this.stores);
  }

  _registerGlobalOverlayEvents() {
    subscribe(this._sendKeyboardEventsToOverlayDropins);
  }

  _setSelectivelyRenderAnnotFlag(data) {
    const { config } = this.props;
    let selectivelyRenderAnnotsFlag;
    if (!isUndefined(config)) {
      selectivelyRenderAnnotsFlag = this.selectivelyRenderAnnotsFlag || config.selectivelyRenderAnnotsFlag;
    }
    if (!isUndefined(selectivelyRenderAnnotsFlag)) {
      const shouldIncludeFormsInRendition = !getShouldRenderAcroForm(this.stores);
      if (shouldIncludeFormsInRendition) {
        selectivelyRenderAnnotsFlag = BigInt(selectivelyRenderAnnotsFlag) | BigInt(AnnotType.WIDGET);
      }
      this.stores.documentStore.setSelectivelyRenderAnnotsFlag(selectivelyRenderAnnotsFlag);
    } else {
      if (isPersonalCommentingEnabled(this.stores)) {
        const commonCommentAnnotationTypes =
          BigInt(AnnotType.TEXT) |
          BigInt(AnnotType.HIGHLIGHT) |
          BigInt(AnnotType.UNDERLINE) |
          BigInt(AnnotType.STRIKEOUT) |
          BigInt(AnnotType.INK);

        const enableFreetextQueryParam = getQueryParameterFromUrl(window.location.href, "enableFreetext");
        this.stores.documentStore.setFreetextStatus(enableFreetextQueryParam === "true");

        if (enableFreetextQueryParam === "true") {
          if (getShouldRenderAcroForm(this.stores)) {
            this.stores.documentStore.setSelectivelyRenderAnnotsFlag(
              BigInt(AnnotType.ALL) ^
                (commonCommentAnnotationTypes | BigInt(AnnotType.FREE_TEXT) | BigInt(AnnotType.WIDGET))
            );
          } else {
            this.stores.documentStore.setSelectivelyRenderAnnotsFlag(
              BigInt(AnnotType.ALL) ^ (commonCommentAnnotationTypes | BigInt(AnnotType.FREE_TEXT))
            );
          }
        } else {
          if (getShouldRenderAcroForm(this.stores)) {
            this.stores.documentStore.setSelectivelyRenderAnnotsFlag(
              BigInt(AnnotType.ALL) ^ (commonCommentAnnotationTypes | BigInt(AnnotType.WIDGET))
            );
          } else {
            this.stores.documentStore.setSelectivelyRenderAnnotsFlag(
              BigInt(AnnotType.ALL) ^ commonCommentAnnotationTypes
            );
          }
        }
      } else {
        if (getShouldRenderAcroForm(this.stores)) {
          this.stores.documentStore.setSelectivelyRenderAnnotsFlag(BigInt(AnnotType.ALL) ^ BigInt(AnnotType.WIDGET));
        }
      }
    }
  }

  _preloadPageAtLowRes() {
    let extraParams = { frameWidth: getViewPortWidth(this.stores) };

    extraParams.showAnnots = getAnnotationFlags(this.stores);
    extraParams.hiddenOCGs = getHiddenOCGs(this.stores);

    extraParams.useSystemFonts = getQueryParameterFromUrl(window.location.href, "useSystemFonts") !== "false";
    const dpi = isCurrentDeviceMobile() ? 1 : getDevicePixelRatio();
    const zoomLevel = constants.DEFAULT_ZOOM_LEVEL;
    recordPageLoadStartTime(0);

    let { pageIndex } = getOpenAction(this.stores);
    const pagesInViewObj = pagesInView(this.stores);
    const hcColorPallete = getHCColorPallete(this.stores);
    pageIndex = pageIndex || 0;
    let response = docApi.getRendition(
      "page",
      pageIndex,
      zoomLevel,
      pagesInViewObj.start,
      pagesInViewObj.end,
      dpi,
      constants.PAGE_PRIORITY,
      extraParams,
      true,
      hcColorPallete
    );
    if (!(response instanceof Promise)) {
      if (response.ajs instanceof Promise) {
        response = response.ajs;
      } else {
        return;
      }
    }

    response
      .then(data => {
        extraParams.renderTime = data.timeTaken;
        extraParams.queueWaitTime = data.queueWaitTime;
        extraParams.ajsTotalTime = data.totalTime;
        extraParams.mainToWorkerTime = data.mainToWorkerTime;
        extraParams.workerToMainTime = data.workerToMainTime;
        const resp = data.data;
        if (resp) {
          const imgUrl = createURLFromBuffer(resp.buffer, resp.mimeType);
          const height = resp.height ? resp.height / dpi : 0;
          const width = resp.width ? resp.width / dpi : 0;
          this.stores.documentStore.pageIsLoaded({
            pageIndex,
            bitmapUrl: imgUrl,
            height,
            width,
            bitmapSize: resp.buffer.byteLength,
            pagesInView: pagesInViewObj,
            pageLoader: "ajs",
            extraParams,
            isPartial: false,
            priority: constants.PAGE_PRIORITY,
            zoomLevel
          });
          if (isInitialPageLoaded(this.stores) === false) {
            this.stores.pagedViewState.setInitialPageStatus(pageIndex, true);
          }
        }
      })
      .catch(noop);
    this._pagesBeingPreloaded.push(pageIndex);

    if (docApi.isExternalAsset()) {
      subscribeToSelector(this.stores, isInitialPageLoaded, this._reloadFirstPage.bind(this));
    }
  }

  _loadOverlayDropins() {
    const overlaysManager = getOverlaysManager(this.stores);
    overlaysManager.loadOverlayDropins();
    this._registerGlobalOverlayEvents();
  }

  _isJSPresentAtDocLevel(actionsData) {
    for (const eventName in actionsData) {
      const actionData = actionsData[eventName];
      const isJSActionPresentCB = action => action.actionType === PDF_ACTIONS.JAVASCRIPT;
      return actionData.some(isJSActionPresentCB);
    }
    return false;
  }

  _isJSPresentAtPageLevel(pageIndexes) {
    return pageIndexes.length !== 0;
  }

  _sendNamedDestinationsDataToServer(data) {
    const namedDestinationCount = data.namedDestinationCount;
    logNamedDestinationData({ namedDestinationCount });
  }

  _sendPageActionsDataToServer(data) {
    if (data && data.pageLevelActions) {
      const docLevel = getDocLevelJsTriggerPoints(data);
      const {
        jsAction: { open, close, pageIndexes = [] } = {},
        nonJSAction: { open: openNonJS, close: closeNonJS, pageIndexes: nonJSPageIndexes = [] } = {}
      } = data.pageLevelActions;
      const pagesInViewObj = pagesInView(this.stores);

      let numPagesInViewHavingJS = 0;
      const numPagesHavingJS = pageIndexes.length;
      const isJSPresentInInitialPage = pageIndexes.includes(getInitialPageIndex(this.stores));

      let numPagesInViewHavingNonJS = 0;
      const numPagesHavingNonJS = nonJSPageIndexes.length;
      const isNonJSPresentInInitialPage = nonJSPageIndexes.includes(getInitialPageIndex(this.stores));

      for (let index = pagesInViewObj.start; index <= pagesInViewObj.end; index++) {
        const isJSPresent = pageIndexes.includes(index);
        if (isJSPresent) {
          numPagesInViewHavingJS++;
        }

        const isNonJSPresent = nonJSPageIndexes.includes(index);
        if (isNonJSPresent) {
          numPagesInViewHavingNonJS++;
        }
      }

      const pageLevelJs = {
        open,
        close,
        isJSPresentInInitialPage,
        numPagesHavingJS,
        numPagesInViewHavingJS
      };

      const pageLevelNonJs = {
        open: openNonJS,
        close: closeNonJS,
        isNonJSPresentInInitialPage,
        numPagesHavingNonJS,
        numPagesInViewHavingNonJS
      };
      logJSActionsData({ triggerPointsHavingJS: { docLevel, pageLevel: pageLevelJs } });
      logNonJSActionTriggerPoints({ pageLevel: pageLevelNonJs });
    }
  }

  trackAnalytics(data) {
    const documentType = getDocumentType(this.stores);
    const permissions = getDocPermissions(this.stores);

    trackIsTagged(data.isFTPDF);
    trackDocumentType(documentType);
    trackDocLevelJSTriggerPoints(getDocLevelJsTriggerPoints(data));
    trackHasCommentsdata(data);
    trackIsScannedDocument();
    trackHasAttachmentsMetadata(data);
    trackPDFTitleMetadata(data);
    trackNamedDestinationCountMetadata(data);
    trackHasBookmarksMetadata(data);
    trackHasDigSigMetadata(data);
    trackHasBaseURLMetadata(data);
    trackLanguageMetadata(data);
    trackHasValidLinDictMetadata(data);
    if (data.hasValidLinDict) {
      trackLinDictEKeyValueMetadata(data);
    }
    trackIsLinearizedWorkflow(data);
    trackPermissionsMetadata(permissions.toString(2));
    if (data.docInfo) {
      trackProducerMetadata(data);
      trackCreatorMetadata(data);
      trackCreationDateMetadata(data);
    }
    trackPageCountMetadata(data);
    trackDocumentSizeMetaData(data);
    trackFileSize(data);
  }

  _loadOverlayDropinsIfNeeded() {
    if (!this.overlayDropinLoaded) {
      this._loadOverlayDropins();
      this.overlayDropinLoaded = true;
    }
  }

  _getCurrentPageGeometry() {
    let pageNum = getCurrentPageNumber(this.stores);
    if (pageNum < 1) {
      pageNum = 1;
    }
    return docApi.getPageRangeGeometry(pageNum, pageNum).then(resp => resp.data.pages);
  }

  _getAllPagesGeometry(data) {
    return docApi.getPageRangeGeometry(1, data.numPages).then(resp => resp.data);
  }

  _trackDocLevelNonJSActions(actions) {
    if (actions && actions.Open) {
      const docOpenActions = actions.Open;
      const nonJSActions = docOpenActions
        ? docOpenActions.filter(action => action.actionType !== PDF_ACTIONS.JAVASCRIPT)
        : [];
      const numNonJSActions = nonJSActions.length;
      numNonJSActions > 0 && trackDocLevelNonJSActions(numNonJSActions > 1);
    }
  }

  async _handleJsEngineLoadAndDocActions(data) {
    const { actions, pageLevelActions: { jsAction } = {}, needAppearances } = data;
    const isJSPresentAtDocLevel = this._isJSPresentAtDocLevel(actions);
    const isJSPresentAtPageLevel =
      jsAction && jsAction.pageIndexes && this._isJSPresentAtPageLevel(jsAction.pageIndexes);
    if (isJSPresentAtDocLevel) {
      try {
        recordDocJsEngineInitStart();
        await this.initJSEngineAndLoadAcroFormDropin(this.stores);
        recordDocJsEngineInitEnd();
        this.isJSEngineLoaded = true;
      } catch (err) {
        console.error(err);
      }
    }
    executeDocOpenActions(this.stores);
    // If js was not present at doc level but at pageLevel or need appreaces is required, then load js engine

    if (!this.isJSEngineLoaded && (isJSPresentAtPageLevel || needAppearances)) {
      try {
        recordPageJsEngineInitStart();
        await this.initJSEngineAndLoadAcroFormDropin(this.stores);
        recordPageJsEngineInitEnd();
      } catch (err) {
        console.error(err);
      }
    }
    this._sendPageActionsDataToServer(data);

    this._trackDocLevelNonJSActions(actions);

    this.stores.documentStore.setJSActionsAnalyticsData({
      hasDocJS: isJSPresentAtDocLevel,
      hasPageJS: isJSPresentAtPageLevel
    });

    if (!getShouldRenderAcroForm(this.stores)) {
      trackDocAndFormJSActions();
    }
  }

  _unblockUIFeaturesAndSetPagesGeometry(data) {
    return Promise.all([docApi.isCompletePDFBufferAvailable(), initialPageLoadedPromise]).then(() => {
      // Triggering event so that preview can enable some of its blocked UI features as well
      triggerCompletePDFBufferAvailableEvent();

      this.stores.documentStore.setShouldUnblockUIFeatures(true); // unblock UI features once complete PDF buffer is available and initial page is loaded

      // set correct page geometry for all the pages in case of openLinearizedPDF once complete PDF buffer is available and initial page is loaded
      if (getIsLinearizedWorkflow(this.stores)) {
        return this._getAllPagesGeometry(data).then(async ({ pages, pageLevelActions }) => {
          Object.assign(data, { pages, pageLevelActions });
          const pagesGeometry = populatePagesGeometryData(data);
          this.stores.documentStore.setPagesGeometry(pagesGeometry);
          await this._handleJsEngineLoadAndDocActions(data);
        });
      }

      return Promise.resolve();
    });
  }

  async initJSEngineAndLoadAcroFormDropin(stores) {
    const jsEngine = await initJSEngine(stores);
    if (!jsEngine) return Promise.reject("Unable to initialize JS Engine");

    if (!getShouldRenderAcroForm(this.stores)) return Promise.reject("Unable to load Acroform Dropin");

    const acroformDropinStartTime = performance.now();
    const overlaysManager = getOverlaysManager(stores);
    // need to handle cases when acroform is disabled using props, or disabled in file. Will be handled in below JIRA
    // ToDo: https://jira.corp.adobe.com/browse/ADCS-22689
    await overlaysManager.loadAcroformDropin();
    await getAcroformDropinMountedPromise(stores).promise;
    const acroformDropinLoadTime = performance.now() - acroformDropinStartTime;
    logAcroformLoadTime(acroformDropinLoadTime);
  }

  async _getMetadataAJSDone(data) {
    recordMetadataEnd({
      openPDFWasmTime: data && data.openPDFWasmTime,
      openPDFQueueWaitTime: data && data.openPDFQueueWaitTime,
      openPDFTotalTime: data && data.openPDFTotalTime,
      openPDFMainToWorker: data && data.openPDFMainToWorkerTime,
      openPDFWorkerToMain: data && data.openPDFWorkerToMainTime
    });
    docApi.setFiletype("application/pdf");
    docApi.setAJSMetaDataStatus(AJSMetadataStatus.SUCCEEDED);
    data = data.data || data;
    data.fileExtension = "pdf";
    if (this._didPDFOpenPasswordMatch(data)) {
      this._passwordAttempts !== 0 && trackPasswordDialogEvents(this._passwordAttempts, "Submitted");
      this._passwordAttempts === 0 && trackSecurityType(securityType.NONE);
      this._loadOverlayDropinsIfNeeded();
      this._setMetadataInStore(data);
      this.stores.documentStore.setPermittedOperations(data.permittedOperations);
      if (data.isFTPDF) {
        this.stores.documentStore.setIsFTPDF(data.isFTPDF);
      }
      if (data.isTagged) {
        this.stores.documentStore.setIsTaggedPDF(data.isTagged);
      }
      dialogApi.hide();
      this.stores.documentStore.setIsLinearizedWorkflow(!data.pages); // openLinearizedPDF does not return page geometry in response
      this.stores.documentStore.setHasFASFields(data.hasFASFields);

      if (getIsLinearizedWorkflow(this.stores)) {
        data.pages = await this._getCurrentPageGeometry(); // get page geometry for the current page in case of openLinearizedPDF
        data.isLinearizedWorkflow = true;
      } else {
        data.isLinearizedWorkflow = false;
      }

      this._unblockUIFeaturesAndSetPagesGeometry(data);
      if (this.props.config.shouldUseMobileModernViewer) {
        this.selectivelyRenderAnnotsFlag = await enableCommentingOnMobile(this.stores);
      }
      this._startRenderingPDF(data);
      // Wait for pages geometry to be set in case of non linearized flow and then execute doc open actions
      if (!getIsLinearizedWorkflow(this.stores)) {
        await this._handleJsEngineLoadAndDocActions(data);
      }
      this.stores.documentStore.setAJSMetaDataStatus(AJSMetadataStatus.SUCCEEDED);
    } else {
      this._retryWithPassword();
      return;
    }
    // Adding check to send analytics only after isInitialPageLoaded
    subscribeToSelector(this.stores, isInitialPageLoaded, async () => {
      if (isInitialPageLoaded(this.stores)) {
        this.trackAnalytics(data);
        const { acrobatJSVersion, enableWasmExceptions } = data;
        logAJSInfo({ acrobatJSVersion, enableWasmExceptions });
        this._sendNamedDestinationsDataToServer(data);
        sendPageLoadEventData();
      }
    });
  }

  _getMetadataExternalDone(data) {
    if (this.props.config.forceSetExternalMetadata || data.connectorType) {
      this._setMetadataFromSecondary(data, true);
    }
    this._getSecondaryMetadataDone(data, true);
  }

  _getMetadataDCAPIDone(data) {
    this._getSecondaryMetadataDone(data);
  }

  _logDocumentAge(data) {
    if (!data || !data.created) {
      return;
    }

    const uploaded = new Date(data.created);
    const documentAge = Date.now() - uploaded;

    const payload = {
      documentAge,
      type: data.type,
      uploadTime: uploaded.getTime(),
      isShared: data.is_shared
    };

    const megaVerb = getQueryParameterFromUrl(window.location.href, "viewer!megaVerb");
    if (megaVerb) {
      payload.megaVerb = megaVerb;
    }

    const defaultRHPFeature = getQueryParameterFromUrl(window.location.href, "defaultRHPFeature");
    if (defaultRHPFeature) {
      payload.defaultRHPFeature = defaultRHPFeature;
    }
    logDocumentAge(payload);
  }

  _getSecondaryMetadataDone(data, isExternal = false) {
    if (!isUndefined(data) && !isUndefined(data.type)) {
      const analyticsData = {};
      analyticsData.file_type = data.type || "unknown";
      let fileExtension = getNamePartsFromFullName(data.fileName).fileExtension;
      fileExtension = !isUndefined(fileExtension) && fileExtension !== "" ? fileExtension : "unknown";
      let pageNumber = !isUndefined(data.last_pagenum) ? data.last_pagenum : "unknown";
      analyticsData.pageOnWhichDocumentOpened = pageNumber;
      analyticsData.fileExtension = fileExtension;

      if (data.type !== "application/pdf") {
        this.shouldOpenInFixedWidthMode = false;
        this.stores.overlayDropinStore.setShouldRenderPersonalCommentsDropin(false);
        if ("numPages" in data) {
          analyticsData.page_count = data.numPages;
          if (data.hasPermsDict) {
            analyticsData.hasPermsDict = true;
          }
        }
      }
      if (isAlternateAssetURIAvailable(this.stores)) {
        this.stores.documentStore.setViewMode(docViewModes.NON_PDF);
      }
      this._loadOverlayDropinsIfNeeded();
      if (isUndefined(data.numPages)) {
        if (data.type !== "application/pdf") {
          if (
            (isExternal && this.externalMetadataTries === constants.EXTERNAL_METADATA_MAX_TRIES) ||
            (!isExternal && this.dcapiMetadataTries === constants.DCAPI_METADATA_MAX_TRIES)
          ) {
            logSecondaryMetadataFailed(dcapiMetadataFailure.NUM_PAGES_UNDEFINED, isExternal);
            this.stores.documentViewStore.setNoPreviewErrorCode(httpErrorCodes.INTERNAL_SERVER_ERROR);
            this.stores.documentStore.setViewMode(docViewModes.NO_PREVIEW);
          } else {
            isExternal ? (this.externalMetadataTries += 1) : (this.dcapiMetadataTries += 1);
            const getMetaDataPromise = docApi.getMetaData({ forceCacheless: true });
            docAPIResponseHandler(
              getMetaDataPromise,
              () => {},
              this._getMetadataFailed,
              isExternal ? this._getMetadataExternalDone : this._getMetadataDCAPIDone
            );
          }
        } else {
          const getMetaDataPromise = docApi.getMetaDataFromDCAPI({
            assetUri: getAssetUri(),
            enableRetryForPDFs: true,
            forceCacheless: true
          });
          getMetaDataPromise.then(
            data => {
              isExternal ? this._getMetadataExternalDone(data) : this._getMetadataDCAPIDone(data);
            },
            () => {
              isExternal ? this._getExternalMetadataFailed() : this._getDCAPIMetadataFailed();
            }
          );
        }
      } else {
        recordExternalMetadataEnd();
        docApi.setSecondaryMetaDataSucceeded(true, isExternal);
        this._setLastPageNumInPagesView(data.last_pagenum);
        this._setMetadataFromSecondary(data, isExternal);
        this.stores.documentStore.setSecondaryMetaDataStatus(SecondaryMetadataStatus.SUCCEEDED);
        if (data.type !== "application/pdf") {
          trackFileSize(data);
          this.stores.hudStore.setPrintVisibility(false);
          this.stores.documentStore.setViewMode(docViewModes.NON_PDF);
          this._startSecondaryRendering(data, isExternal);
        } else if (!docApi.getAJSMetaDataSucceeded() && docApi.shouldUseRasterRenditions()) {
          this._startSecondaryRendering(data, isExternal);
        }
      }

      this._logDocumentAge(data);
    } else {
      recordExternalMetadataEnd();
      logSecondaryMetadataFailed(dcapiMetadataFailure.DOC_TYPE_UNDEFINED, isExternal);
    }
  }

  _setLastPageNumInPagesView(lastPageIndex) {
    if (!isUndefined(lastPageIndex)) {
      docApi.saveLastPageNumReceivedInMetadata(lastPageIndex);
      this.stores.pagedViewState.setOpenAction({
        pageIndex: lastPageIndex,
        x: 0,
        y: 0
      });
    }
  }

  _setMetadataFromSecondary(data, isExternal = false) {
    if (!isUndefined(data) && !isUndefined(data.name)) {
      !isUndefined(data.type) && docApi.setFiletype(data.type);
      const { fileName, fileExtension } = getNamePartsFromFullName(data.name);
      this.stores.documentStore.setMetadata({
        title: getTitleFromConfigProp(this.props.config),
        fileName,
        fileExtension,
        fileSize: data.size,
        numPages: data.numPages
      });
      this.stores.documentStore.setFileMetaData(data);
      if (data.fileStorageType) {
        this.stores.documentStore.setFileStorageType(data.fileStorageType);
      }
      if (data.fileShareType) {
        this.stores.documentStore.setFileShareType(data.fileShareType);
      }
    }
  }

  _getMetadataFailed(data, ajsResponse, dcapiResponse) {
    const ajsRejected = ajsResponse === "rejected";
    const dcapiRejected = dcapiResponse === "rejected";
    if (ajsRejected) {
      docApi.setAJSMetaDataStatus(AJSMetadataStatus.FAILED);
      this.stores.documentStore.setAJSMetaDataStatus(AJSMetadataStatus.FAILED);
      trackAJSMetadataFailure();
    }
    if (ajsRejected && dcapiRejected) {
      this._showCorruptDialog(loadFailureType.CORRUPT_PDF);
      triggerPreviewErrorEvent({
        errorType: loadFailureType.CORRUPT_PDF
      });
    } else if (ajsRejected) {
      if (getFileSize(this.stores) === 0) {
        this._showCorruptDialog(loadFailureType.FILE_SIZE);
        triggerPreviewErrorEvent({
          errorType: loadFailureType.FILE_SIZE
        });
      } else if (data && data.error && data.error === "corruptFile") {
        trackDocumentLoadFailed(loadFailureType.UNSUPPORTED_FEATURES_DIALOG);
        triggerPreviewErrorEvent({
          errorType: loadFailureType.UNSUPPORTED_FEATURES_DIALOG
        });
        dialogApi.show(dialogTypes.UNSUPPORTED_FEATURES_DIALOG, {
          cancelDialog: () => {
            dialogApi.hide();
          }
        });
      }
    }
  }

  _getExternalMetadataFailed(error, ajsResponse, externalResponse) {
    this.stores.documentStore.setSecondaryMetaDataStatus(SecondaryMetadataStatus.FAILED);
    logSecondaryMetadataFailed(dcapiMetadataFailure.PROMISE_REJECTED, true);
  }

  _getDCAPIMetadataFailed(error, ajsResponse, dcapiResponse) {
    // DCAPIMetadataFailed: promise rejected
    this.stores.documentStore.setSecondaryMetaDataStatus(SecondaryMetadataStatus.FAILED);
    logSecondaryMetadataFailed(dcapiMetadataFailure.PROMISE_REJECTED);
  }

  _showCorruptDialog(failureType) {
    // TODO: integrate react-intl and separate strings constants.
    dialogApi.show(dialogTypes.FAILURE_DIALOG, {
      failureString: "dialog.failure.corruptFile",
      label: "dialog.failure.title",
      confirmLabel: "common.ok",
      cancelDialog: () => {
        dialogApi.hide();
      }
    });
    trackDocumentLoadFailed(failureType);
    this.stores.documentViewStore.setNoPreviewErrorCode(noPreviewErrorCodes.CORRUPT_PDF);
    this.stores.documentStore.setViewMode(docViewModes.NO_PREVIEW);
  }

  _resetCacheHitAndMissTimeout() {
    clearTimeout(this.cacheHitAndMissTimeout);
    this.cacheHitAndMissTimeout = setTimeout(this._logCacheHitAndMissInfo, this.cacheHitAndMissLogTime);
  }

  _logCacheHitAndMissInfo() {
    this.sessionTime += this.cacheHitAndMissLogTime;
    logCacheHitAndMissInfo({
      ...getCacheHitAndMissCount(this.stores),
      sessionTime: this.sessionTime / 1000 / 60 /* session time in minute */
    });
    this.cacheHitAndMissLogTime *= 2;
    this._resetCacheHitAndMissTimeout();
  }

  _trackOverPrintType(numpages) {
    const promises = [];
    for (let i = 0; i < numpages && i < 5; i++) {
      promises.push(docApi.getOverprintType(i));
    }

    Promise.all(promises).then(responses => {
      const extractedValues = responses.map(item => item.overprintType);
      trackOverprintType(extractedValues);
    });
  }

  _startRenderingPDF(data) {
    this._setDocPermissions(data);
    this._setSelectivelyRenderAnnotFlag(data);
    this._setZoomLimits(data);
    if (this.props.config.enableLowResolutionRendition && docApi.isExternalAsset()) {
      this._preloadPageAtLowRes();
    }
    this._processPagesData(data);
    subscribeToSelector(this.stores, isInitialPageLoaded, () => {
      if (isInitialPageLoaded(this.stores)) {
        this._startPageCaching();
        // TODO: below call will be removed once analytics is completed
        this.stores.documentStore.countYouTubeLinks();
        this.stores.documentStore.setInitialPageLoadTime();
        this.getPageRenditionLogTimeout = setTimeout(() => {
          this.stores.documentStore.setLogGetPageRenditionInfo(false);
          logGetPageRenditionCallInfo(getPageRenditionCallInfo(this.stores));
        }, GET_PAGE_RENDITION_LOG_TIMEOUT);
        if (!getHasDigSigFromMetadata(this.stores)) {
          showDocPermissionsToast();
        }
        this.cacheHitAndMissTimeout = setTimeout(this._logCacheHitAndMissInfo, this.cacheHitAndMissLogTime);
        this._trackOverPrintType(data.numPages);
      }
    });
  }

  _setZoomLimits(data) {
    // const { maxZoom, minZoom } = data.pageGeometryJson;
    const useReactVirtualized = getUseReactVirtualized(this.stores);
    const minMaxZoomObj = getMinMaxZoomLimits(useReactVirtualized, data);
    this.stores.zoom.setZoomLimits(minMaxZoomObj);
  }

  _setInitialPageZoomLevel(maxZoom, initialPageZoomLevel) {
    if (isIntegrationContext(getAppContext(this.stores)) && maxZoom) {
      const defaultZoomLevel = getDefaultZoomLevel(this.stores);
      initialPageZoomLevel = getMinZoomFromDefaultAndMaxZoom(defaultZoomLevel, maxZoom);
    }
    if (shouldAllowInitialZoomLevelChange(this.stores)) {
      this.stores.zoom.setPageZoomLevel(initialPageZoomLevel);
    }
  }

  _populatePagesInStore(
    { numPages = 1, pageGeometries, maxZoom },
    initialPageZoomLevel = constants.DEFAULT_ZOOM_LEVEL
  ) {
    let pages = [];
    for (let i = 0; i < numPages; i++) {
      pages.push({
        ...getOriginalPageDimensions(pageGeometries, numPages, i),
        index: i,
        id: i,
        initialStatus: this._pagesBeingPreloaded.indexOf(i) === -1 ? loadStatus.NOT_LOADED : loadStatus.BEING_PRELOADED
      });
    }
    if (this._shouldOpenInFixedWidthMode()) {
      this._setZoomMargins();
      runInAction(() => {
        this.stores.documentStore.addPages(pages);
        this.stores.zoom.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIXED_WIDTH });
      });
    } else {
      this.stores.documentStore.addPages(pages);
    }
    this._setInitialPageZoomLevel(maxZoom, initialPageZoomLevel);
  }

  _startSecondaryRendering(data, isExternal = false) {
    if (!isUndefined(data)) {
      const numPages = data.numPages > 0 ? data.numPages : 1;
      const pageIndex = data.last_pagenum >= 0 && data.last_pagenum < numPages ? data.last_pagenum : 0;

      recordExternalPageLoadStartTime(pageIndex);
      logFirstRasterRequested(performance.now() - getPreviewInitTime());

      if (data.connectorType === "GDrive" && data.type !== "application/pdf") {
        this.stores.documentViewStore.setNoPreviewErrorCode(httpErrorCodes.UNSUPPORTED_MEDIA_TYPE);
        this.stores.documentStore.setViewMode(docViewModes.NO_PREVIEW);
      } else {
        let extraParams = {
          size: getDCAPIRenditionSize(this.stores),
          format: getDCAPIRenditionFormat(this.stores)
        };

        let renditionPromise;
        const hcColorPallete = getHCColorPallete(this.stores);
        renditionPromise = docApi.getRendition(
          "page",
          pageIndex,
          1,
          pageIndex,
          pageIndex,
          getDevicePixelRatio(),
          constants.PAGE_PRIORITY,
          isExternal ? {} : extraParams,
          false,
          hcColorPallete
        );
        renditionPromise = isExternal ? renditionPromise.external : renditionPromise.dcapi;

        renditionPromise
          .then(renditionResponse => {
            const resp = renditionResponse.params;
            if (resp && !docApi.getAJSMetaDataSucceeded()) {
              const imgUrl = createURLFromBuffer(resp.pageImgBuffer, resp.mimeType);
              ajsMakeCancellablePromise(getImageDimensionsFromBlobUrl(imgUrl)).then(imageDimensions => {
                if (!docApi.getAJSMetaDataSucceeded()) {
                  let initialZoomLevel = 1;
                  if (isReviewFile(this.stores, envUtil)) {
                    // Setting zoom level for fit-width mode in review files
                    initialZoomLevel =
                      (getViewPortWidthWithoutPagesViewScrollbar(this.stores) -
                        getExpandedPanelWidth(this.stores) -
                        this.stores.zoom.getMarginsDiff()) /
                      imageDimensions.width;
                  }
                  const height = imageDimensions.height
                    ? imageDimensions.height * initialZoomLevel
                    : DEFAULT_PAGE_HEIGHT;
                  const width = imageDimensions.width ? imageDimensions.width * initialZoomLevel : DEFAULT_PAGE_WIDTH;

                  this._populatePagesInStore({
                    numPages: data.numPages,
                    pageGeometries: {
                      height,
                      width
                    }
                  });
                  if (isCurrentDeviceMobile()) {
                    this.stores.zoom.setZoomLimits({
                      minZoom: getMinZoomLevelForMobile(this.stores)
                    });
                  }
                  this.stores.documentStore.pageIsLoaded({
                    pageIndex,
                    bitmapUrl: imgUrl,
                    height,
                    width,
                    bitmapSize: resp.pageImgBuffer.byteLength
                  });
                  performAppSetWidthOpenAction(this.stores);
                  if (
                    (!isExternal && isFirstDCAPIRenditionLoaded(this.stores) === false) ||
                    (isExternal && isFirstExternalAPIRenditionLoaded(this.stores) === false)
                  ) {
                    recordFirstRasterLoaded();
                    recordExternalPageLoadEndTime(pageIndex);
                    logFirstRasterRendition(getFirstRasterLoadTime());
                    logFirstRenditionTypeVisible(getFirstRasterLoadTime(), "Raster");
                    !isExternal && this.stores.dcapiStore.setFirstDCAPIRenditionStatus(true);
                    isExternal && this.stores.externalStore.setFirstExternalAPIRenditionStatus(true);
                    this.stores.documentStore.setFirstRasterSize(resp.pageImgBuffer.byteLength);
                  }
                }
              });
            }
          })
          .catch(errorResponse => {
            if (
              errorResponse.code &&
              errorResponse.code >= httpErrorCodes.BAD_REQUEST &&
              docApi.getFiletype() !== "application/pdf"
            ) {
              this.stores.documentViewStore.setNoPreviewErrorCode(errorResponse.code);
              this.stores.documentStore.setViewMode(docViewModes.NO_PREVIEW);
            }
          });
      }
      subscribeToSelector(
        this.stores,
        isExternal ? isFirstExternalAPIRenditionLoaded : isFirstDCAPIRenditionLoaded,
        this._startPageCaching.bind(this)
      );
    }
  }

  _setDocPermissions(data) {
    this.stores.documentStore.setIsDocumentEncrypted(data.isEncrypted);
    this.stores.documentStore.setDocumentHasSigPerms(data.hasPermsDict);
    if (data.permissions) this.stores.documentStore.setDocPermissions(data.permissions);
    else this.stores.documentStore.setDocPermissions(data.isCopyAllowed << constants.COPY_TEXT_PERM);
  }

  _startPageCaching() {
    const zoomlevel = getCurrentPageZoomLevel(this.stores);
    this.stores.documentStore.setPagesCacheWindow(zoomlevel);
    const pagesCacheManager = getPagesCacheManager();
    if (pagesCacheManager) {
      pagesCacheManager.cacheItems();
    }
  }

  _shouldSetZoomMargins(fixedWidthLeftMargin, fixedWidthRightMargin) {
    return (
      (!this.shouldSupportPersonalComments || this.shouldOpenInFixedWidthMode) &&
      !isUndefined(fixedWidthLeftMargin) &&
      !isUndefined(fixedWidthRightMargin) &&
      (this.props.config.shouldRenderCommentsDropin ? getIsCommentsPanelVisible(this.stores) : true)
    );
  }

  _setZoomMargins() {
    if (this.props.config) {
      const { fixedWidthLeftMargin, fixedWidthRightMargin } = getFixedWidthValues(this.stores, this.props.config);
      if (this._shouldSetZoomMargins(fixedWidthLeftMargin, fixedWidthRightMargin)) {
        this.stores.zoom.setFixedWidthMargin(fixedWidthLeftMargin, fixedWidthRightMargin);
      }
    }
  }

  _shouldOpenInFixedWidthMode() {
    if (this.props.config) {
      const { fixedWidthLeftMargin, fixedWidthRightMargin } = this.props.config;
      return (
        (!this.shouldSupportPersonalComments || this.shouldOpenInFixedWidthMode) &&
        !isUndefined(fixedWidthLeftMargin) &&
        !isUndefined(fixedWidthRightMargin)
      );
    }

    return false;
  }

  _setZoomLevelLimitsAfterAJSMetadataSucceeds(data) {
    const { shouldUseMobileModernViewer } = this.props.config;
    let docMinZoom = constants.DEFAULT_MIN_ZOOM * getDevicePixelRatio();
    let docMaxZoom = constants.DEFAULT_MAX_ZOOM * getDevicePixelRatio();
    const maxZoom = getMaxZoomFromAllPages(data);
    const minZoom = getMinZoomFromAllPages(data);

    docMinZoom = minZoom ? Math.max(docMinZoom, minZoom) : docMinZoom;
    docMaxZoom = maxZoom ? Math.min(docMaxZoom, maxZoom) : docMaxZoom;
    if (isCurrentDeviceMobile() || shouldUseMobileModernViewer) {
      docMinZoom = getMinZoomLevelForMobile(this.stores, docMinZoom, docMaxZoom);
    }
    const allowedMaxZoom = calculateMaxZoomForIEEdge(getUseReactVirtualized(this.stores), data);
    docMaxZoom = Math.min(docMaxZoom, allowedMaxZoom);
    this.stores.zoom.setZoomLimits({
      minZoom: docMinZoom,
      maxZoom: docMaxZoom
    });
  }

  _getInitialPageZoomLevel(data, currPageIndex) {
    let initialPageZoomLevel = getPageZoomLevel(this.stores, currPageIndex);
    if (data.pages && shouldAllowInitialZoomLevelChange(this.stores)) {
      // below function runs if we get data from dcapi first and we need to calculate zoomlevel
      if (docApi.shouldUseRasterRenditions()) {
        initialPageZoomLevel = calculateZoomLevelAccToCurrPageRasterSize(
          getDCAPIRenditionSize(this.stores),
          data,
          currPageIndex
        );
      }
    }
    return initialPageZoomLevel;
  }

  _getNewPageZoomLevel(initialPageZoomLevel, currPageIndex, data) {
    let { currPageHeight, currPageWidth } = getPageHeightAndWidthFromPageGeometry(this.stores, data);
    let newZoomLevel = initialPageZoomLevel;

    if (shouldAllowInitialZoomLevelChange(this.stores)) {
      newZoomLevel = Math.min(getPageHeight(this.stores, currPageIndex) / currPageHeight, getMaxZoom(this.stores));
      newZoomLevel = Math.min(getPageWidth(this.stores, currPageIndex) / currPageWidth, newZoomLevel);
    }
    return newZoomLevel;
  }

  _unloadPageAndThumbnailOnAJSMetadataSuccess() {
    for (let i = 0; i < getTotalPages(this.stores); i++) {
      this.stores.documentStore.unloadPage(i);
      this.stores.documentStore.unloadThumbnail(i);
    }
  }

  _loadPagesInViewInStore(newZoomLevel, oldPageDimensions) {
    if (newZoomLevel === getCurrentPageZoomLevel(this.stores)) {
      const pagesInViewObj = pagesInView(this.stores);
      for (let i = pagesInViewObj.start; i <= pagesInViewObj.end; i++) {
        this.stores.documentStore.loadPage(i, getPageZoomLevel(this.stores, i), pagesInViewObj);
      }
    } else if (shouldAllowInitialZoomLevelChange(this.stores)) {
      const { pageIndex, pageBottomMargin, yInPage, xInPage } = oldPageDimensions;
      this.stores.zoom.performCustomZoom(
        { pageIndex, xInPage, yInPage, pageBottomMargin },
        { newZoomLevel, newPageViewModifier: getPageViewModifier(this.stores) }
      );
    }
  }

  _loadThumbnailsInViewInStore() {
    if (currLHPFeature(this.stores) === lhpFeatures.THUMBNAIL) {
      const thumbnailsInViewObj = thumbnailsInView(this.stores);
      this.stores.documentStore.loadThumbnailsInView(thumbnailsInViewObj);
    }
  }

  _processPagesData(data) {
    // called only when AJS metadata has succeeded
    const currPageIndex = getCurrentPageIndex(this.stores);
    const initialPageZoomLevel = this._getInitialPageZoomLevel(data, currPageIndex);

    if (getTotalPages(this.stores) === 0) {
      // will be true if dcapi metadata has not come yet
      if (data.pages) {
        const dpi = isCurrentDeviceMobile() ? 1 : getDevicePixelRatio();
        if (constants.DEFAULT_ZOOM_LEVEL * dpi > getMaxZoom(this.stores)) {
          this._pagesBeingPreloaded = [];
        }
      }

      runInAction(() => {
        this._populatePagesInStore(
          {
            numPages: data.numPages,
            pageGeometries: data.pages,
            maxZoom: getMaxZoom(this.stores)
          },
          initialPageZoomLevel
        );
        performAppSetWidthOpenAction(this.stores);
      });
    } else {
      this._unloadPageAndThumbnailOnAJSMetadataSuccess();
      const pagesGeometry = populatePagesGeometryData(data);
      runInAction(() => {
        const newZoomLevel = this._getNewPageZoomLevel(initialPageZoomLevel, getCurrentPageIndex(this.stores), data);
        let { pageIndex, pageBottomMargin, yInPage } = getPageBeforeOrAtOffset(this.stores);
        let xInPage = getPagesViewXPos(this.stores) / newZoomLevel;
        yInPage /= newZoomLevel;
        const oldPageDimensions = { pageIndex, pageBottomMargin, yInPage, xInPage };
        this.stores.documentStore.setPagesGeometry(pagesGeometry);
        this._loadPagesInViewInStore(newZoomLevel, oldPageDimensions);
        this._loadThumbnailsInViewInStore();
      });
    }
    this._setZoomLevelLimitsAfterAJSMetadataSucceeds(data);
    logAJSRenderingStart(performance.now());
  }

  _reloadFirstPage() {
    if (isInitialPageLoaded(this.stores) === true) {
      let { pageIndex } = getOpenAction(this.stores);
      pageIndex = pageIndex || 0;
      this.stores.documentStore.unloadPage(pageIndex);
      this.stores.documentStore.loadPage(
        pageIndex,
        getPageZoomLevel(this.stores, pageIndex),
        pagesInView(this.stores),
        constants.PAGE_PRIORITY,
        { firstPageReloaded: true, isFirstPageReloadCall: true }
      );
    }
  }

  _setMetadataInStore(data) {
    const urlParams = getUrlVars(window.location.href);
    const metadata = {};
    metadata.fileExtension = data.fileExtension;
    if (getQueryParameterFromUrl(window.location.href, "useDCAPI") === "false") {
      // Incase of dropin when useDCAPI=false, try to set filename from assetUri
      let name = getLastPartFromPath(urlParams.assetUri);
      const { fileName, fileExtension } = getNamePartsFromFullName(name);
      metadata.fileName = fileName;
      metadata.fileExtension = fileExtension;
    }
    metadata.actions = data.actions || {};
    metadata.pdfTitle = data.pdfTitle ? data.pdfTitle : "";
    metadata.fileSize = data.fileSize || 0;
    metadata.title = getTitleFromConfigProp(this.props.config) || this._getTitle(data, metadata.fileName);
    metadata.language = this._getPDFLanguage(data);
    metadata.displayDocTitle = data.displayDocTitle;
    metadata.isXFA = data.isXFA;
    metadata.hasAcroFormFields = data.hasAcroFormFields;
    metadata.hasBookmarks = data.hasBookmarks;
    metadata.hasAttachments = data.hasAttachments;
    metadata.hasDigitalSignatures = data.hasDigitalSignatures;
    metadata.isEditingAllowed = data.isEditingAllowed;
    metadata.isPortfolio = data.isPortfolio;
    metadata.docInfo = data.docInfo;
    metadata.needAppearances = data.needAppearances ? data.needAppearances : false;
    metadata.pageLevelActions = data.pageLevelActions;
    metadata.baseURL = data.baseURL;
    metadata.co = data.co || [];
    metadata.pdfVersion = data.pdfVersion;
    metadata.numPages = data.numPages;

    // <TODO>:: (handling of the case when switchStrategy is 2, is pending)
    this.stores.documentStore.setMetadata(metadata);
    this.stores.lhpStore.attachmentsModel.setHasAttachments(data.hasAttachments);
  }

  _getPDFLanguage(data) {
    return data.language || "";
  }

  _getTitle(data, fileName) {
    let title = "";
    if (data.pdfTitle) {
      title = data.pdfTitle;
    } else if (fileName) {
      title = fileName;
    }

    return title;
  }

  _onPasswordCancelled() {
    this.stores.documentViewStore.setNoPreviewErrorCode(noPreviewErrorCodes.PASSWORD_CANCELLED);
    this.stores.documentStore.setViewMode(docViewModes.NO_PREVIEW);
    this.stores.documentStore.setQuickToolsVisibility(false);
    this.stores.documentStore.setRhpVisibility(false);
  }

  _retryWithPassword() {
    this.stores.documentStore.setIsFilePasswordProtected(true);
    this.stores.documentStore.setLoadFailed(constants.PASSWORD_PROTECTED_DOC_STRING);
    // If the file is password protected, all preload messages will be rejected.
    const shouldsuppressPasswordDialog = getSuppressPasswordDialog(this.stores);
    const shouldDisablePasswordCancelButton = getShouldDisableProtectedPdfCancel(this.stores);
    this._pagesBeingPreloaded = [];
    // No password dialog comes if the it is supressed from the view-sdk-bundle. This will redirect to cancelled.
    if (shouldsuppressPasswordDialog) {
      trackSecurityType(securityType.PASSWORD_PROTECTED);
      this._onPasswordCancelled();
      triggerPreviewErrorEvent({
        errorType: failureType.SUPPRESSED_PASSWORD_DIALOG
      });
    } // currently we do infinite number of password reattempts.
    else if (this._passwordAttempts === 0) {
      trackSecurityType(securityType.PASSWORD_PROTECTED);
      dialogApi.show(dialogTypes.INPUT_DIALOG, {
        onSubmit: this._onPasswordSubmit,
        onChange: () => {
          dialogApi.setDialogHasError(false);
        },
        onCancel: () => {
          trackPasswordDialogEvents(this._passwordAttempts, "Cancelled");
          dialogApi.hide();
          this._onPasswordCancelled();
          triggerPreviewErrorEvent({
            errorType: failureType.NO_PASSWORD_PROVIDED
          });
        },
        localeStrings: {
          primaryText: {
            str: "dialog.password.text",
            values: {}
          },
          secondaryText: "dialog.password.incorrect",
          title: "dialog.password.title",
          confirmLabel: "dialog.password.input.submit",
          cancelLabel: "common.cancel",
          placeholder: "dialog.password.input.placeholder"
        },
        dialogType: "password",
        id: "document-password-dialog",
        dontShowCancelButtonformatMessage: shouldDisablePasswordCancelButton
      });
    } else {
      dialogApi.setDialogHasError(true);
    }
  }

  _onPasswordSubmit(password) {
    this.stores.documentStore.unsetLoadFailure();
    dialogApi.setDialogHasError(false);
    this._passwordAttempts++;
    docAPIResponseHandler(
      docApi.getMetaData({ password, forceCacheless: true }),
      this._getMetadataAJSDone,
      this._getMetadataFailed
    );
  }

  _didPDFOpenPasswordMatch(data) {
    return isUndefined(data.errorCode) || data.errorCode !== errorCodes.INCORRECT_PASSWORD;
  }

  resetTimeout() {
    clearTimeout(this.getPageRenditionLogTimeout);
  }
}

export { RenderingManager };
